// ─────────────────────────────────────────────────────────────────────────────
// PolicyRules.js — Admin / Superadmin: manage expense policy rules
//
// View all active rules, create new rules, edit threshold/action/message,
// and deactivate rules (soft-delete). Live-enforced at expense-save time.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

// ── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = ["Travel","Hotel","Food","Fuel","Courier","Stationeries","Prints","Miscellaneous","all"];
const SUBCATEGORIES = {
  Food:    ["Breakfast","Lunch","Dinner","Snacks","Tea/Coffee/Juice","Other"],
  Travel:  ["Train","Cab","Auto","Bike Taxi","Cab/Auto","Flight","Others"],
  Courier: ["Within City","Other State","Other Country"],
};
const RULE_TYPES = [
  { value: "single_expense_limit", label: "Single Expense Limit",  desc: "Max amount per individual expense entry"        },
  { value: "daily_limit",          label: "Daily Category Limit",  desc: "Max total for this category in a single day"    },
  { value: "trip_limit",           label: "Trip Category Limit",   desc: "Max total for this category across the whole trip" },
];
const CAT_ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌", all:"📋" };
const ACTION_CONFIG = {
  warn:  { label:"⚠️ Warn",  bg:"#FEF3C7", color:"#D97706", border:"#F59E0B", desc:"Expense saves but employee sees a warning" },
  block: { label:"🚫 Block", bg:"#FEE2E2", color:"#DC2626", border:"#F87171", desc:"Expense is rejected until admin approves"  },
};

const EMPTY_FORM = {
  company: "all", category: "Food", subcategory: "", rule_type: "single_expense_limit",
  threshold_amount: "", action: "warn", message: "",
};

function fmtInr(n) {
  return parseFloat(n || 0).toLocaleString("en-IN");
}

// ── Rule Form Modal ──────────────────────────────────────────────────────────
function RuleModal({ rule, isSuperadmin, onClose, onSaved }) {
  const isEdit = !!rule?.id;
  const [form,   setForm]   = useState(rule || EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.threshold_amount || parseFloat(form.threshold_amount) <= 0) {
      return toast.error("Enter a valid threshold amount");
    }
    setSaving(true);
    try {
      if (isEdit) {
        await API.put(`/policy/rules/${rule.id}`, {
          threshold_amount: parseFloat(form.threshold_amount),
          action:           form.action,
          message:          form.message || null,
        });
        toast.success("Rule updated!");
      } else {
        await API.post("/policy/rules", {
          company:          form.company,
          category:         form.category,
          subcategory:      form.subcategory || null,
          rule_type:        form.rule_type,
          threshold_amount: parseFloat(form.threshold_amount),
          action:           form.action,
          message:          form.message || null,
        });
        toast.success("Rule created!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    padding: "9px 12px", borderRadius: 8,
    border: "1.5px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.06)", color: "#f9fafb",
    fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box",
    outline: "none",
  };
  const sel = { ...inp, colorScheme: "dark" };
  const label = { fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6, display: "block" };

  const subcatOptions = SUBCATEGORIES[form.category] || [];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9200, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
      onClick={onClose}>
      <div style={{ background:"#1e2235", borderRadius:16, padding:28, maxWidth:560, width:"100%", border:"1px solid rgba(255,255,255,0.1)", maxHeight:"90vh", overflowY:"auto" }}
        onClick={e => e.stopPropagation()}>

        <div style={{ fontSize:17, fontWeight:800, color:"#f9fafb", marginBottom:4 }}>
          {isEdit ? "✏️ Edit Policy Rule" : "➕ Create Policy Rule"}
        </div>
        <div style={{ fontSize:12, color:"#9ca3af", marginBottom:24 }}>
          {isEdit ? "Update the threshold, action, or message for this rule."
                  : "Rules are enforced when employees save expenses."}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>

          {/* Company — superadmin only, locked for admin */}
          {isSuperadmin && !isEdit && (
            <div style={{ gridColumn:"1/-1" }}>
              <label style={label}>Company</label>
              <select style={sel} value={form.company} onChange={e => set("company", e.target.value)}>
                <option value="all">All Companies</option>
                <option value="ebek">Ebek</option>
                <option value="ebekmed">Ebek Med</option>
              </select>
            </div>
          )}

          {/* Category */}
          {!isEdit && (
            <div>
              <label style={label}>Category</label>
              <select style={sel} value={form.category}
                onChange={e => { set("category", e.target.value); set("subcategory", ""); }}>
                {CATEGORIES.filter(c => c !== "all").map(c => (
                  <option key={c} value={c}>{CAT_ICONS[c]} {c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Subcategory */}
          {!isEdit && (
            <div>
              <label style={label}>Subcategory <span style={{ fontWeight:400, color:"#6b7280" }}>(optional)</span></label>
              <select style={sel} value={form.subcategory} onChange={e => set("subcategory", e.target.value)}
                disabled={!subcatOptions.length}>
                <option value="">— Any subcategory —</option>
                {subcatOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          {/* Rule Type */}
          {!isEdit && (
            <div style={{ gridColumn:"1/-1" }}>
              <label style={label}>Rule Type</label>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {RULE_TYPES.map(rt => (
                  <label key={rt.value} style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", padding:"10px 14px", borderRadius:8, border:`1.5px solid ${form.rule_type===rt.value?"rgba(129,140,248,.7)":"rgba(255,255,255,0.1)"}`, background: form.rule_type===rt.value ? "rgba(129,140,248,0.1)" : "transparent", transition:"all .15s" }}>
                    <input type="radio" name="rule_type" value={rt.value} checked={form.rule_type===rt.value} onChange={() => set("rule_type", rt.value)} style={{ marginTop:2, accentColor:"#818cf8" }} />
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:"#f9fafb" }}>{rt.label}</div>
                      <div style={{ fontSize:11, color:"#9ca3af" }}>{rt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Threshold */}
          <div>
            <label style={label}>Threshold Amount (₹)</label>
            <input type="number" style={inp} placeholder="e.g. 500" min="1"
              value={form.threshold_amount} onChange={e => set("threshold_amount", e.target.value)} />
          </div>

          {/* Action */}
          <div>
            <label style={label}>Action when exceeded</label>
            <div style={{ display:"flex", gap:8 }}>
              {Object.entries(ACTION_CONFIG).map(([k, cfg]) => (
                <button key={k} type="button"
                  onClick={() => set("action", k)}
                  style={{ flex:1, padding:"9px 8px", borderRadius:8, border:`1.5px solid ${form.action===k ? cfg.border : "rgba(255,255,255,0.12)"}`, background: form.action===k ? cfg.bg : "transparent", color: form.action===k ? cfg.color : "#9ca3af", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all .15s" }}>
                  {cfg.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:6 }}>{ACTION_CONFIG[form.action].desc}</div>
          </div>

          {/* Message */}
          <div style={{ gridColumn:"1/-1" }}>
            <label style={label}>Message shown to employee <span style={{ fontWeight:400, color:"#6b7280" }}>(optional)</span></label>
            <textarea style={{ ...inp, resize:"vertical", minHeight:56 }} rows={2}
              placeholder="e.g. Hotel above ₹3,000/night — please attach manager approval"
              value={form.message} onChange={e => set("message", e.target.value)} />
          </div>
        </div>

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:22 }}>
          <button onClick={onClose} style={{ padding:"9px 18px", borderRadius:8, border:"1.5px solid rgba(255,255,255,0.15)", background:"transparent", color:"#9ca3af", fontSize:13, cursor:"pointer", fontWeight:600 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"9px 22px", borderRadius:8, border:"none", background:"#6366f1", color:"white", fontWeight:700, fontSize:13, cursor:saving?"not-allowed":"pointer", opacity:saving?0.7:1 }}>
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function PolicyRules() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";

  const [rules,       setRules]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterCat,   setFilterCat]   = useState("all");
  const [filterAction,setFilterAction]= useState("all");
  const [showModal,   setShowModal]   = useState(false);
  const [editRule,    setEditRule]    = useState(null);
  const [deactivating,setDeactivating]= useState(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/policy/rules");
      setRules(res.data);
    } catch { toast.error("Failed to load policy rules"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleDeactivate = async (ruleId) => {
    if (!window.confirm("Deactivate this rule? Employees will no longer be checked against it.")) return;
    setDeactivating(ruleId);
    try {
      await API.delete(`/policy/rules/${ruleId}`);
      toast.success("Rule deactivated");
      setRules(r => r.filter(x => x.id !== ruleId));
    } catch { toast.error("Failed to deactivate"); }
    finally { setDeactivating(null); }
  };

  // ── Filtered view ────────────────────────────────────────────────────────
  const displayed = rules.filter(r => {
    if (filterCat    !== "all" && r.category !== filterCat) return false;
    if (filterAction !== "all" && r.action   !== filterAction) return false;
    return true;
  });

  // ── Summary counts ───────────────────────────────────────────────────────
  const warnCount  = rules.filter(r => r.action === "warn").length;
  const blockCount = rules.filter(r => r.action === "block").length;

  // ── Unique categories present ────────────────────────────────────────────
  const presentCats = ["all", ...new Set(rules.map(r => r.category))];

  // ── Styles ───────────────────────────────────────────────────────────────
  const card = (extra = {}) => ({
    background:"var(--bg-card)", borderRadius:14, border:"1px solid var(--border)", padding:"18px 20px", ...extra,
  });
  const pill = (bg, color) => ({
    display:"inline-flex", alignItems:"center", padding:"3px 10px", borderRadius:20,
    background:bg, color, fontSize:11, fontWeight:700,
  });
  const tabBtn = (active) => ({
    padding:"7px 16px", borderRadius:8, border:"none", cursor:"pointer", fontWeight:700, fontSize:12,
    background: active ? "rgba(99,102,241,0.2)" : "transparent",
    color:       active ? "#818cf8"              : "#9ca3af",
    transition:"all .15s",
  });

  const ruleTypeLabel = (rt) => RULE_TYPES.find(x => x.value === rt)?.label || rt;

  return (
    <div>
      <style>{`
        .pr-table { width:100%; border-collapse:collapse; }
        .pr-table th { padding:10px 14px; text-align:left; font-size:11px; font-weight:700; color:#9ca3af; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid rgba(255,255,255,0.08); }
        .pr-table td { padding:12px 14px; font-size:13px; color:#e5e7eb; border-bottom:1px solid rgba(255,255,255,0.05); vertical-align:middle; }
        .pr-table tr:hover td { background:rgba(255,255,255,0.03); }
        .pr-table tr:last-child td { border-bottom:none; }
        .pr-act-btn { padding:5px 12px; borderRadius:7px; border:none; cursor:pointer; font-size:12px; font-weight:700; transition:all .15s; }
        @media(max-width:700px){.pr-table thead{display:none}.pr-table tr{display:block;margin-bottom:12px;border:1px solid rgba(255,255,255,0.1);border-radius:10px;padding:10px;} .pr-table td{display:flex;justify-content:space-between;border:none;padding:6px 8px;}}
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 className="page-title">📋 Expense Policy Rules</h1>
          <p className="page-subtitle">
            Define spending limits per category. <strong>Warn</strong> lets employees proceed with a notice; <strong>Block</strong> rejects the expense until approved.
          </p>
        </div>
        <button
          onClick={() => { setEditRule(null); setShowModal(true); }}
          style={{ padding:"10px 20px", borderRadius:10, border:"none", background:"#6366f1", color:"white", fontWeight:700, fontSize:14, cursor:"pointer", whiteSpace:"nowrap" }}>
          ➕ Add Rule
        </button>
      </div>

      {/* ── Summary KPIs ────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:20 }}>
        {[
          { label:"Total Active Rules", val:rules.length,  color:"#818cf8" },
          { label:"Warn Rules",         val:warnCount,     color:"#F59E0B" },
          { label:"Block Rules",        val:blockCount,    color:"#EF4444" },
          { label:"Categories Covered", val:new Set(rules.map(r=>r.category)).size, color:"#34d399" },
        ].map(k => (
          <div key={k.label} style={{ ...card(), textAlign:"center" }}>
            <div style={{ fontSize:28, fontWeight:900, color:k.color }}>{k.val}</div>
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:4, fontWeight:600 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div style={{ ...card(), marginBottom:16, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginRight:4 }}>CATEGORY</div>
        {presentCats.map(c => (
          <button key={c} style={tabBtn(filterCat===c)} onClick={() => setFilterCat(c)}>
            {CAT_ICONS[c] || ""} {c === "all" ? "All" : c}
          </button>
        ))}
        <div style={{ width:1, height:24, background:"rgba(255,255,255,0.1)", margin:"0 8px" }} />
        <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginRight:4 }}>ACTION</div>
        {["all","warn","block"].map(a => (
          <button key={a} style={tabBtn(filterAction===a)} onClick={() => setFilterAction(a)}>
            {a==="all" ? "All" : a==="warn" ? "⚠️ Warn" : "🚫 Block"}
          </button>
        ))}
        <div style={{ flex:1 }} />
        <div style={{ fontSize:12, color:"#9ca3af" }}>{displayed.length} rule{displayed.length!==1?"s":""}</div>
      </div>

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign:"center", padding:"60px 20px" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
          <div style={{ color:"#9ca3af" }}>Loading policy rules...</div>
        </div>
      )}

      {/* ── Empty ────────────────────────────────────────────────────────── */}
      {!loading && displayed.length === 0 && (
        <div style={{ ...card(), textAlign:"center", padding:"48px 24px" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
          <div style={{ fontSize:16, fontWeight:700, color:"#d1d5db" }}>No rules found</div>
          <div style={{ fontSize:13, color:"#9ca3af", marginTop:6 }}>
            {filterCat !== "all" || filterAction !== "all" ? "Try clearing the filters." : "Click \"Add Rule\" to create your first policy rule."}
          </div>
        </div>
      )}

      {/* ── Rules Table ──────────────────────────────────────────────────── */}
      {!loading && displayed.length > 0 && (
        <div style={card({ padding:0, overflow:"hidden" })}>
          <table className="pr-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Rule Type</th>
                <th>Threshold</th>
                <th>Action</th>
                <th style={{ maxWidth:280 }}>Message</th>
                {isSuperadmin && <th>Company</th>}
                <th style={{ textAlign:"right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(rule => {
                const act = ACTION_CONFIG[rule.action];
                return (
                  <tr key={rule.id}>
                    <td>
                      <span style={{ fontSize:15 }}>{CAT_ICONS[rule.category] || "📌"}</span>{" "}
                      <span style={{ fontWeight:700, color:"#f9fafb" }}>{rule.category}</span>
                    </td>
                    <td style={{ color: rule.subcategory ? "#d1d5db" : "#6b7280", fontStyle: rule.subcategory ? "normal" : "italic" }}>
                      {rule.subcategory || "Any"}
                    </td>
                    <td>
                      <span style={{ fontSize:12, padding:"3px 8px", borderRadius:6, background:"rgba(129,140,248,0.15)", color:"#a5b4fc", fontWeight:600 }}>
                        {ruleTypeLabel(rule.rule_type)}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight:800, fontSize:15, color:"#f9fafb" }}>₹{fmtInr(rule.threshold_amount)}</span>
                    </td>
                    <td>
                      <span style={{ ...pill(act.bg, act.color), border:`1px solid ${act.border}` }}>
                        {act.label}
                      </span>
                    </td>
                    <td style={{ maxWidth:280, fontSize:12, color:"#9ca3af", fontStyle:"italic", wordBreak:"break-word" }}>
                      {rule.message || <span style={{ color:"#4b5563" }}>—</span>}
                    </td>
                    {isSuperadmin && (
                      <td>
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, fontWeight:700,
                          background: rule.company==="all"?"#EDE9FE":rule.company==="ebekmed"?"#CCFBF1":"#FEE2E2",
                          color:      rule.company==="all"?"#7C3AED":rule.company==="ebekmed"?"#0F6E56":"#B91C1C" }}>
                          {rule.company === "all" ? "All" : rule.company === "ebekmed" ? "Ebek Med" : "Ebek"}
                        </span>
                      </td>
                    )}
                    <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>
                      <button
                        onClick={() => { setEditRule({ ...rule, threshold_amount: rule.threshold_amount }); setShowModal(true); }}
                        style={{ padding:"5px 12px", borderRadius:7, border:"1.5px solid rgba(129,140,248,0.4)", background:"rgba(129,140,248,0.1)", color:"#a5b4fc", fontSize:12, cursor:"pointer", fontWeight:700, marginRight:6 }}>
                        ✏️ Edit
                      </button>
                      <button
                        disabled={deactivating === rule.id}
                        onClick={() => handleDeactivate(rule.id)}
                        style={{ padding:"5px 12px", borderRadius:7, border:"1.5px solid rgba(239,68,68,0.4)", background:"rgba(239,68,68,0.08)", color:"#f87171", fontSize:12, cursor:"pointer", fontWeight:700, opacity: deactivating===rule.id ? 0.5 : 1 }}>
                        {deactivating === rule.id ? "..." : "🗑️ Off"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Info box ────────────────────────────────────────────────────── */}
      {!loading && (
        <div style={{ ...card({ marginTop:16, borderLeft:"3px solid #6366f1" }) }}>
          <div style={{ fontSize:12, color:"#9ca3af", lineHeight:1.8 }}>
            <strong style={{ color:"#d1d5db" }}>How rules work:</strong>{" "}
            When an employee saves an expense, the system checks all active rules matching their company and category.
            A <span style={{ color:"#F59E0B", fontWeight:700 }}>Warn</span> rule saves the expense but shows the employee an amber alert — finance can still review it.
            A <span style={{ color:"#EF4444", fontWeight:700 }}>Block</span> rule rejects the save outright with a red error message — the employee must seek manager approval first.
            Rules apply to both companies unless a specific company is set. Subcategory rules (e.g. Lunch, Cab) only apply when the employee selects that sub-type.
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────────────────── */}
      {showModal && (
        <RuleModal
          rule={editRule}
          isSuperadmin={isSuperadmin}
          onClose={() => { setShowModal(false); setEditRule(null); }}
          onSaved={loadRules}
        />
      )}
    </div>
  );
}
